// src/pages/sales/purchase-order-components/po-summary-components/ApprovalSection.jsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  alpha,
  useTheme,
  Divider
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  RemoveCircle as UnapproveIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const ApprovalSection = ({
  data,
  approvalHistory,
  isApproved,
  isRejected,
  allStepsCompleted,
  onApprove,
  onReject,
  onUnapprove,
  processing,
  canApprove = true
}) => {
  const theme = useTheme();
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const { poCreation } = data;

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Card sx={{ 
        mb: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: isApproved 
          ? '#f0f9f4'
          : isRejected
          ? '#ffebee'
          : '#fff8e1',
        boxShadow: `0 4px 12px ${alpha(
          isApproved ? theme.palette.success.main : isRejected ? theme.palette.error.main : theme.palette.warning.main, 
          0.15
        )}`,
        position: 'sticky',
        top: 20,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 20px ${alpha(
            isApproved ? theme.palette.success.main : isRejected ? theme.palette.error.main : theme.palette.warning.main, 
            0.2
          )}`
        }
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: alpha(
                isApproved ? theme.palette.success.main : isRejected ? theme.palette.error.main : theme.palette.warning.main, 
                0.15
              )
            }}>
              {isApproved ? (
                <ApproveIcon sx={{ fontSize: 28, color: 'success.main' }} />
              ) : isRejected ? (
                <RejectIcon sx={{ fontSize: 28, color: 'error.main' }} />
              ) : (
                <InfoIcon sx={{ fontSize: 28, color: 'warning.main' }} />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Approval Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isApproved ? 'Approved for processing' : isRejected ? 'Rejected' : 'Pending approval'}
              </Typography>
            </Box>
          </Box>

          {/* Status Display */}
          {isApproved && poCreation && (
            <Box sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <ApproveIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="subtitle2" fontWeight={500} color="success.main">
                  APPROVED
                </Typography>
              </Box>
              {poCreation.approved_at && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Date:</strong> {formatDateTime(poCreation.approved_at)}
                </Typography>
              )}
              {poCreation.approved_signature && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 1 }}>
                    Approved By
                  </Typography>
                  <Box sx={{ 
                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                    fontSize: '24px', 
                    color: theme.palette.success.dark,
                    mb: 0.5
                  }}>
                    {poCreation.approved_signature.name}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {poCreation.approved_signature.title || 'Sales Representative'}
                  </Typography>
                </Box>
              )}
              {poCreation.approval_notes && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.5 }}>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {poCreation.approval_notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {isRejected && poCreation && (
            <Box sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <RejectIcon sx={{ fontSize: 20, color: 'error.main' }} />
                <Typography variant="subtitle2" fontWeight={500} color="error.main">
                  REJECTED
                </Typography>
              </Box>
              {poCreation.rejected_at && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Date:</strong> {formatDateTime(poCreation.rejected_at)}
                </Typography>
              )}
              {poCreation.rejection_reason && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.5 }}>
                    Reason
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {poCreation.rejection_reason}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {!isApproved && !isRejected && (
            <Box sx={{ 
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InfoIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                <Typography variant="subtitle2" fontWeight={500} color="warning.main">
                  PENDING APPROVAL
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                This proforma invoice is awaiting approval. 
                {!allStepsCompleted && ' Please complete all required steps before approving.'}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {!isApproved && !isRejected && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<ApproveIcon />}
                  onClick={() => canApprove && onApprove()}
                  disabled={!allStepsCompleted || processing || !canApprove}
                  sx={{
                    py: 1.5,
                    fontWeight: 500,
                    borderRadius: 2,
                    opacity: canApprove ? 1 : 0.5,
                    pointerEvents: canApprove ? 'auto' : 'none',
                    cursor: canApprove ? 'pointer' : 'not-allowed',
                    boxShadow: (allStepsCompleted && canApprove) ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` : 'none',
                    '&:hover': {
                      boxShadow: (allStepsCompleted && canApprove) ? `0 6px 16px ${alpha(theme.palette.success.main, 0.4)}` : 'none'
                    }
                  }}
                >
                  {processing ? 'Approving...' : 'Approve Order'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<RejectIcon />}
                  onClick={() => canApprove && onReject()}
                  disabled={processing || !canApprove}
                  sx={{
                    py: 1.5,
                    fontWeight: 500,
                    borderRadius: 2,
                    opacity: canApprove ? 1 : 0.5,
                    pointerEvents: canApprove ? 'auto' : 'none',
                    cursor: canApprove ? 'pointer' : 'not-allowed'
                  }}
                >
                  {processing ? 'Rejecting...' : 'Reject'}
                </Button>
              </>
            )}

            {isApproved && (
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                startIcon={<UnapproveIcon />}
                onClick={() => canApprove && onUnapprove()}
                disabled={processing || !canApprove}
                sx={{
                  py: 1.5,
                  fontWeight: 500,
                  borderRadius: 2,
                  opacity: canApprove ? 1 : 0.5,
                  pointerEvents: canApprove ? 'auto' : 'none',
                  cursor: canApprove ? 'pointer' : 'not-allowed'
                }}
              >
                {processing ? 'Removing Approval...' : 'Remove Approval'}
              </Button>
            )}

            {isRejected && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<ApproveIcon />}
                onClick={() => canApprove && onApprove()}
                disabled={!allStepsCompleted || processing || !canApprove}
                sx={{
                  py: 1.5,
                  fontWeight: 500,
                  borderRadius: 2,
                  opacity: canApprove ? 1 : 0.5,
                  pointerEvents: canApprove ? 'auto' : 'none',
                  cursor: canApprove ? 'pointer' : 'not-allowed'
                }}
              >
                {processing ? 'Approving...' : 'Approve Anyway'}
              </Button>
            )}

            {/* View History Button */}
            <Button
              variant="text"
              fullWidth
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryDialogOpen(true)}
              sx={{
                py: 1.5,
                fontWeight: 500,
                borderRadius: 2,
                color: 'text.secondary'
              }}
            >
              View Approval History ({approvalHistory.length})
            </Button>
          </Box>

          {/* No Permission Alert */}
          {!canApprove && (
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                You don't have approval permissions
              </Typography>
              <Typography variant="caption">
                Contact your administrator to request approval permissions.
              </Typography>
            </Alert>
          )}

          {/* Requirements Checklist */}
          {!allStepsCompleted && (
            <Box sx={{ 
              mt: 3,
              pt: 3,
              borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 1.5 }}>
                Requirements
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={data.purchaseOrder?.stage_completed?.includes('order_creation') ? 'Complete' : 'Pending'}
                    color={data.purchaseOrder?.stage_completed?.includes('order_creation') ? 'success' : 'default'}
                    size="small"
                    sx={{ minWidth: 80 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Order Creation
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={data.purchaseOrder?.stage_completed?.includes('product_selection') ? 'Complete' : 'Pending'}
                    color={data.purchaseOrder?.stage_completed?.includes('product_selection') ? 'success' : 'default'}
                    size="small"
                    sx={{ minWidth: 80 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Product Selection
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={data.purchaseOrder?.stage_completed?.includes('po_creation') ? 'Complete' : 'Pending'}
                    color={data.purchaseOrder?.stage_completed?.includes('po_creation') ? 'success' : 'default'}
                    size="small"
                    sx={{ minWidth: 80 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    PI Creation
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={data.purchaseOrder?.stage_completed?.includes('delivery_info') ? 'Complete' : 'Pending'}
                    color={data.purchaseOrder?.stage_completed?.includes('delivery_info') ? 'success' : 'default'}
                    size="small"
                    sx={{ minWidth: 80 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Delivery Information
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Approval History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 500,
          color: 'primary.main'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryIcon />
            <Typography variant="h6" fontWeight={500}>
              Approval History
            </Typography>
          </Box>
          <IconButton onClick={() => setHistoryDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {approvalHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No approval history available
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                    <TableCell sx={{ fontWeight: 500 }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>Signature</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>Performed By</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>Notes/Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvalHistory.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDateTime(entry.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.action_type?.toUpperCase()}
                          color={
                            entry.action_type === 'approved' ? 'success' : 
                            entry.action_type === 'rejected' ? 'error' : 
                            'default'
                          }
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                          fontSize: '18px', 
                          color: entry.action_type === 'approved' ? theme.palette.success.dark : theme.palette.text.primary,
                          mb: 0.5
                        }}>
                          {entry.signature_name}
                        </Box>
                        {entry.signature_title && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {entry.signature_title}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {entry.performed_by_name || 'Unknown'}
                        </Typography>
                        {entry.performed_by_email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {entry.performed_by_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {entry.notes || entry.reason || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApprovalSection;